import { useCallback, useMemo, useRef, useState } from 'react';
import type { BillingFormFields } from '@justifi/webcomponents/dist/loader';
import { getClientSecretLink, PaymentMethod, PaymentPlanQueries } from '@frontend/api-payment-plans';
import { useTranslation } from '@frontend/i18n';
import {
  justifiTokenizePayloadToPaymentMethod,
  TokenizePaymentMethodErrorCallbackPayloads,
  TokenizePaymentMethodSubmitSuccessEvent,
  UseJustifiTokenizePaymentMethod,
  useJustifiTokenizePaymentMethod,
} from '@frontend/payments-justifi-wrapper';
import { BaseFieldProps, ButtonProps, useFormField } from '@frontend/design-system';
import { useScopedTrackingIds } from '../../collect-payment-multi/hooks/use-scoped-tracking-ids';

export type UseAddCardOnFileJustifiProps = {
  locationId: string;
  personId?: string;
  onError?: (message: string) => void;
  onCardSaved: (paymentMethod: PaymentMethod) => void;
  onCancel: () => void;
};

export type UseAddCardOnFileJustifi = {
  onSubmit: () => Promise<void>;
  resetFormState: () => void;
  fillBillingForm: (data: BillingFormFields) => Promise<void>;
  primary: ButtonProps;
  secondary: ButtonProps;
  shouldShowFooter: boolean;
  shouldShowLoading: boolean;
  justifiProps: UseJustifiTokenizePaymentMethod['justifiProps'];
  elementRef: UseJustifiTokenizePaymentMethod['elementRef'];
  nameFieldProps: BaseFieldProps<string, '', HTMLInputElement>;
};

export const useAddCardOnFileJustifi = ({
  locationId,
  personId,
  onError,
  onCancel,
  onCardSaved,
}: UseAddCardOnFileJustifiProps) => {
  const { t } = useTranslation('payments');

  const [nameFieldKey, setNameFieldKey] = useState(0);
  const nameFieldProps = useFormField({ type: 'text', required: true, value: '', touched: false }, [nameFieldKey]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const submittingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { invalidatePaymentMethodsByPerson } = PaymentPlanQueries.usePaymentPlansInvalidation();

  const { data: setupLink } = PaymentPlanQueries.useGetPaymentMethodSetupLink({ locationId });

  const startSubmittingTimeout = () => {
    submittingTimeoutRef.current = setTimeout(() => {
      setIsSubmitting(false);
    }, 2000);
  };

  const clearSubmittingTimeout = () => {
    if (submittingTimeoutRef.current) {
      clearTimeout(submittingTimeoutRef.current);
      submittingTimeoutRef.current = null;
    }
  };

  const onSubmitCallback = useCallback(
    async (data: TokenizePaymentMethodSubmitSuccessEvent) => {
      clearSubmittingTimeout();

      setIsSubmitting(true);
      const paymentMethod = justifiTokenizePayloadToPaymentMethod(data);

      if (!setupLink) {
        return onError?.(t('Setup link is not available.'));
      }
      try {
        await getClientSecretLink(setupLink, {
          data: {
            personId,
            locationId,
            paymentMethodId: paymentMethod.id,
          },
        });

        onCardSaved(paymentMethod);

        invalidatePaymentMethodsByPerson(personId, locationId);
      } catch (error) {
        console.error('Error saving payment method:', error);
        onError?.(t('An error occurred while saving the payment method.'));
      }

      setIsSubmitting(false);
    },
    [onCardSaved, personId, setupLink, onError, locationId]
  );

  const onErrorCallback = useCallback(
    (data: TokenizePaymentMethodErrorCallbackPayloads) => {
      if (data.message) {
        return onError?.(data.message);
      }

      return onError?.(t('An error ocurred while saving the payment method.'));
    },
    [onError]
  );

  const { justifiProps, elementRef, fillBillingForm, tokenizePaymentMethod } = useJustifiTokenizePaymentMethod({
    locationId,
    cardholderName: nameFieldProps.value,
    justifiProps: {
      disableBankAccount: true, // only show card option
      hideCardBillingForm: true, // no billing form, only zip
    },
    onSubmit: onSubmitCallback,
    onError: onErrorCallback,
  });

  const onClickSubmit = useCallback(async () => {
    setIsSubmitting(true);
    await tokenizePaymentMethod();
    startSubmittingTimeout();
  }, [tokenizePaymentMethod, setIsSubmitting]);

  const resetFormState = useCallback(() => {
    setNameFieldKey((prev) => prev + 1);
  }, []);

  const { primaryBtn, secondaryBtn } = useScopedTrackingIds({
    primaryBtn: 'card-on-file--justifi--add-card--save',
    secondaryBtn: 'card-on-file--justifi--add-card--cancel',
  });

  const value = useMemo<UseAddCardOnFileJustifi>(() => {
    return {
      fillBillingForm,
      onSubmit: onClickSubmit,
      resetFormState,
      nameFieldProps,
      primary: {
        disabled: isSubmitting || nameFieldProps['aria-invalid'] || !nameFieldProps.value,
        trackingId: primaryBtn,
        children: t('Save'),
      },
      secondary: {
        trackingId: secondaryBtn,
        children: t('Back'),
      },
      shouldShowFooter: true,
      shouldShowLoading: isSubmitting || !justifiProps.accountId,
      elementRef,
      justifiProps,
    };
  }, [onClickSubmit, onCancel, fillBillingForm, isSubmitting, justifiProps, nameFieldProps]);

  return value;
};
